import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../ui/Button/Button';
import { ConfirmModal } from '../../../components/ConfirmModal/ConfirmModal';
import { CustomEmailTemplateModal } from '../../../features/relance/customEmailTemplate/CustomEmailTemplateModal';
import { selectHasCustomTemplate, useCustomEmailTemplateStore } from '../../../stores/customEmailTemplateStore';
import { TranslationKey } from '../../../i18n/translationKeys';
import { CustomEmailTemplatePreview } from './CustomEmailTemplatePreview';

type PanelStatus = 'loading' | 'ready' | 'load-error';

export const CustomEmailTemplatePanel: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { template, fetch, remove } = useCustomEmailTemplateStore();
    const hasTemplate = useCustomEmailTemplateStore(selectHasCustomTemplate);
    const [status, setStatus] = useState<PanelStatus>('loading');
    const [editing, setEditing] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleteFailed, setDeleteFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetch()
            .then(() => { if (!cancelled) setStatus('ready'); })
            .catch(e => {
                console.error('Failed to load the custom email template:', e);
                if (!cancelled) setStatus('load-error');
            });
        return () => { cancelled = true; };
    }, [fetch]);

    const handleDelete = async () => {
        setConfirmingDelete(false);
        setDeleteFailed(false);
        try {
            await remove();
        } catch (e) {
            console.error('Failed to delete the custom email template:', e);
            setDeleteFailed(true);
        }
    };

    if (status === 'loading') return null;

    return (
        <div className="space-y-s" data-testid="settings-custom-email-template">
            <h2 className="text-block-title text-content">{t(TranslationKey.Settings.CustomEmailTemplate.Title)}</h2>
            <p className="text-primary-700">{t(TranslationKey.Settings.CustomEmailTemplate.Description)}</p>
            {status === 'load-error' ? (
                <p data-testid="settings-custom-email-template-load-error" className="text-danger text-s">
                    {t(TranslationKey.Settings.CustomEmailTemplate.LoadError)}
                </p>
            ) : (
                <>
                    {hasTemplate
                        ? <CustomEmailTemplatePreview template={template} />
                        : <p data-testid="settings-custom-email-template-empty" className="text-s text-content-secondary">
                            {t(TranslationKey.Settings.CustomEmailTemplate.Empty)}
                        </p>}
                    <div className="flex gap-s pt-xs">
                        <Button data-testid="settings-custom-email-template-edit" onClick={() => setEditing(true)}>
                            {t(hasTemplate ? TranslationKey.Settings.CustomEmailTemplate.Edit : TranslationKey.Settings.CustomEmailTemplate.Create)}
                        </Button>
                        {hasTemplate && (
                            <Button variant="secondary" data-testid="settings-custom-email-template-delete" onClick={() => setConfirmingDelete(true)}>
                                {t(TranslationKey.Settings.CustomEmailTemplate.Delete)}
                            </Button>
                        )}
                    </div>
                    {deleteFailed && (
                        <p data-testid="settings-custom-email-template-delete-error" className="text-danger text-s">
                            {t(TranslationKey.Settings.CustomEmailTemplate.DeleteError)}
                        </p>
                    )}
                </>
            )}
            {editing && (
                <CustomEmailTemplateModal
                    initial={template}
                    initialLanguage={i18n.language === 'en' ? 'EN' : 'FR'}
                    onClose={() => setEditing(false)}
                />
            )}
            {confirmingDelete && (
                <ConfirmModal
                    testId="settings-custom-email-template-delete-modal"
                    heading={t(TranslationKey.Settings.CustomEmailTemplate.DeleteDialog.Heading)}
                    body={t(TranslationKey.Settings.CustomEmailTemplate.DeleteDialog.Body)}
                    confirmLabel={t(TranslationKey.Settings.CustomEmailTemplate.DeleteDialog.Confirm)}
                    cancelLabel={t(TranslationKey.Settings.CustomEmailTemplate.DeleteDialog.Cancel)}
                    onConfirm={() => void handleDelete()}
                    onCancel={() => setConfirmingDelete(false)}
                    confirmBtnTestId="settings-custom-email-template-delete-confirm"
                />
            )}
        </div>
    );
};
